import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Role } from "@/types/auth.types";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageHeaderWithBack } from "@/components/common/PageHeaderWithBack";
import { NewUserModal } from "./NewUserModal";
import { EditUserModal } from "./EditUserModal";
import { usersService } from "@/api/services/users.service";
import { useAlert } from "@/hooks/useAlert";
import { capitalizeName } from "@/utils/string";
import styles from "./UserManagement.module.css";
const FILTER_TABS = [
    { id: "all", label: "All Users" },
    { id: "admin", label: "Admin" },
    { id: "staff", label: "Staff" },
];
const getAccessDescription = (access) => {
    if (access.all)
        return "Full system access";
    const enabled = [];
    if (access.sites)
        enabled.push("Sites");
    if (access.quotes)
        enabled.push("Quotes");
    if (access.templates)
        enabled.push("Templates");
    if (access.jobs)
        enabled.push("Jobs");
    if (access.contacts)
        enabled.push("Contacts");
    if (access.estimating)
        enabled.push("Estimating");
    if (enabled.length === 0)
        return "No access";
    return enabled.join(", ");
};
const getRoleLabel = (role) => {
    switch (role) {
        case Role.ADMIN:
            return "Admin";
        case Role.STAFF:
            return "Staff";
        case Role.CLIENT:
            return "Client";
        case Role.CONTRACTOR:
            return "Contractor";
        case Role.SUBCONTRACTOR:
            return "Subcontractor";
        case Role.SURVEYOR:
            return "Surveyor";
        default:
            return role;
    }
};
const getInitials = (userName) => {
    return userName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};
const mapUserResponseToRow = (user) => ({
    id: user.id,
    name: capitalizeName(user.name) || user.email,
    email: user.email,
    role: user.role,
    access: user.access,
    accessDescription: user.accessDescription || getAccessDescription(user.access),
});
export const UserManagement = () => {
    const navigate = useNavigate();
    const { user, refreshUserAccess, logout } = useAuth();
    const { confirm } = useAlert();
    const [activeFilter, setActiveFilter] = useState("all");
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [error, setError] = useState(null);
    const handleBack = useCallback(() => navigate(-1), [navigate]);
    // Fetch users from API
    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const filters = {
                filter: activeFilter,
                search: searchTerm || undefined,
                pageSize: 100, // Get all users for now
            };
            const response = await usersService.getUsers(filters);
            const userRows = response.data.data.map(mapUserResponseToRow);
            setUsers(userRows);
        }
        catch (err) {
            console.error("Failed to fetch users:", err);
            setError("Failed to load users. Please try again.");
            setUsers([]);
        }
        finally {
            setIsLoading(false);
        }
    }, [activeFilter, searchTerm]);
    // Fetch users on mount and when filters change
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);
    // Debounced search
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);
    // Filter users locally for immediate feedback (API already filters, but this handles edge cases)
    const filteredUsers = useMemo(() => {
        const search = debouncedSearchTerm.trim().toLowerCase();
        if (!search)
            return users;
        return users.filter((u) => u.name.toLowerCase().includes(search) ||
            u.email?.toLowerCase().includes(search) ||
            u.accessDescription.toLowerCase().includes(search));
    }, [users, debouncedSearchTerm]);
    const handleManageAccess = useCallback((userRow) => {
        setUserToEdit(userRow);
        setIsEditUserModalOpen(true);
    }, []);
    const handleInviteUser = useCallback(() => {
        setIsNewUserModalOpen(true);
    }, []);
    const handleSaveNewUser = useCallback(async (newUser) => {
        setIsSaving(true);
        setError(null);
        try {
            const response = await usersService.createUser({
                email: newUser.email,
                name: newUser.name,
                role: newUser.role,
            });
            // If Staff role with custom access, update access after creation
            if (newUser.role === Role.STAFF && newUser.access && response.data) {
                await usersService.updateUserAccess(response.data.id, {
                    role: Role.STAFF,
                    accessAll: newUser.access.all,
                    accessSites: newUser.access.sites,
                    accessQuotes: newUser.access.quotes,
                    accessTemplates: newUser.access.templates,
                    accessJobs: newUser.access.jobs,
                    accessContacts: newUser.access.contacts,
                    accessEstimating: newUser.access.estimating,
                    accessCostSettings: newUser.access.costSettings,
                    accessForms: newUser.access.forms,
                    accessUserManagement: newUser.access.userManagement,
                });
            }
            // Refresh the user list
            await fetchUsers();
            setIsNewUserModalOpen(false);
        }
        catch (err) {
            console.error("Failed to create user:", err);
            const errorMessage = err instanceof Error
                ? err.message
                : "Failed to create user. Please try again.";
            setError(errorMessage);
        }
        finally {
            setIsSaving(false);
        }
    }, [fetchUsers]);
    const handleSaveUser = useCallback(async (userId, name, email, role, access) => {
        setIsSaving(true);
        setError(null);
        // Store current user's role before update to detect role changes
        const currentUserRole = user?.role;
        try {
            // Update user with all data in one call
            await usersService.updateUserComplete(userId, {
                name,
                email,
                role,
                accessAll: access.all,
                accessSites: access.sites,
                accessQuotes: access.quotes,
                accessTemplates: access.templates,
                accessJobs: access.jobs,
                accessContacts: access.contacts,
                accessEstimating: access.estimating,
                accessCostSettings: access.costSettings,
                accessForms: access.forms,
                accessUserManagement: access.userManagement,
            });
            // Update local state optimistically
            setUsers((prevUsers) => prevUsers.map((user) => user.id === userId
                ? {
                    ...user,
                    name: capitalizeName(name) || email,
                    role,
                    access,
                    accessDescription: getAccessDescription(access),
                }
                : user));
            // Check if user changed their own role - logout and redirect to login
            if (userId === user?.id && role !== currentUserRole) {
                logout();
                navigate("/login");
                return;
            }
            // Refresh current user's access in case they modified their own permissions
            if (userId === user?.id) {
                await refreshUserAccess();
            }
            setIsEditUserModalOpen(false);
            setUserToEdit(null);
        }
        catch (err) {
            console.error("Failed to update user:", err);
            const errorMessage = err instanceof Error
                ? err.message
                : "Failed to update user. Please try again.";
            setError(errorMessage);
            // Re-fetch users on error to ensure consistency
            await fetchUsers();
        }
        finally {
            setIsSaving(false);
        }
    }, [fetchUsers, user?.id, user?.role, refreshUserAccess, logout, navigate]);
    const handleDeleteUser = useCallback(async (userId) => {
        const confirmed = await confirm("Are you sure you want to remove this user?", "Confirm Deletion");
        if (!confirmed) {
            return;
        }
        setDeletingId(userId);
        setError(null);
        try {
            await usersService.deleteUser(userId);
            // Remove from local state immediately for better UX
            setUsers((prev) => prev.filter((u) => u.id !== userId));
        }
        catch (err) {
            console.error("Failed to delete user:", err);
            const errorMessage = err instanceof Error
                ? err.message
                : "Failed to delete user. Please try again.";
            setError(errorMessage);
        }
        finally {
            setDeletingId(null);
        }
    }, [confirm]);
    return (_jsxs("div", { className: styles.pageWrapper, children: [_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Manage users", breadcrumb: "Admin / Users", onBack: handleBack }), error && (_jsxs("div", { style: {
                            padding: "12px 16px",
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                            borderRadius: "8px",
                            marginBottom: "16px",
                            fontSize: "14px",
                        }, children: [error, _jsx("button", { onClick: () => setError(null), style: {
                                    marginLeft: "12px",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#dc2626",
                                    textDecoration: "underline",
                                }, children: "Dismiss" })] })), _jsxs("div", { className: styles.filterTabs, children: [_jsx("div", { children: FILTER_TABS.map((tab) => (_jsx("button", { className: `${styles.filterTab} ${activeFilter === tab.id ? styles.active : ""}`, onClick: () => setActiveFilter(tab.id), children: tab.label }, tab.id))) }), _jsxs("div", { className: styles.pageActions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search users", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), autoFocus: true })] }), _jsx("button", { className: styles.addButton, onClick: handleInviteUser, children: "New User" })] })] }), _jsx("div", { className: "table-page-listing", children: _jsx("div", { className: "table-container", children: _jsxs("table", { className: "table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "thName", children: "NAME" }), _jsx("th", { children: "EMAIL" }), _jsx("th", { children: "ROLE" }), _jsx("th", { children: "ACCESS" }), _jsx("th", { children: "ACTIONS" }), _jsx("th", {})] }) }), _jsx("tbody", { children: isLoading ? (_jsx("tr", { children: _jsx("td", { colSpan: 6, className: "table-empty-state", children: _jsx("p", { children: "Loading users..." }) }) })) : filteredUsers.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 6, className: "table-empty-state", children: _jsx("p", { children: "No users match this filter." }) }) })) : (filteredUsers.map((userRow) => (_jsxs("tr", { children: [_jsx("td", { className: "tdName", children: _jsxs("div", { className: styles.userNameCell, children: [_jsx("div", { className: styles.userAvatar, children: getInitials(userRow.name) }), _jsx("span", { children: userRow.name })] }) }), _jsx("td", { children: _jsx("span", { children: userRow.email || "—" }) }), _jsx("td", { children: _jsx("span", { children: getRoleLabel(userRow.role) }) }), _jsx("td", { children: _jsx("span", { children: userRow.accessDescription }) }), _jsx("td", { children: _jsxs("button", { type: "button", className: styles.tableActionPrimary, onClick: () => handleManageAccess(userRow), children: [_jsx("span", { className: "material-icons", style: { fontSize: "16px" }, children: "lock_reset" }), "Manage Access"] }) }), _jsx("td", { className: "tdActions", children: _jsxs("button", { type: "button", className: styles.tableActionDangerText, onClick: () => handleDeleteUser(userRow.id), disabled: deletingId === userRow.id, children: [_jsx("span", { className: "material-icons", style: { fontSize: "16px" }, children: "do_not_disturb_on" }), deletingId === userRow.id ? (_jsx("span", { className: styles.spinner })) : ("Delete")] }) })] }, userRow.id)))) })] }) }) })] }), _jsx(NewUserModal, { isOpen: isNewUserModalOpen, onClose: () => setIsNewUserModalOpen(false), onSave: handleSaveNewUser, isSaving: isSaving }), _jsx(EditUserModal, { isOpen: isEditUserModalOpen, onClose: () => {
                    setIsEditUserModalOpen(false);
                    setUserToEdit(null);
                }, onSave: handleSaveUser, user: userToEdit, isSaving: isSaving })] }));
};
